import type { CityConfig } from "@/pages/city-template/types";

export const racineCountyConfig: CityConfig = {
  city: "Racine County",
  state: "Wisconsin",
  stateAbbr: "WI",
  slug: "racine-county",
  route: "/cities/racine-county",
  title: "Emergency & Expedited Passport Services Racine County WI | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Racine County, WI — as fast as 24 hours. No Racine County passport agency. Fast Passport Center couriers to the Chicago Passport Agency at 101 Ida B Wells Drive, about 65 miles south. A+ BBB rated. Serving Mount Pleasant, Caledonia, Sturtevant, Burlington, and all Racine County families heading abroad for business, education, or vacation.",
  h1: "Emergency & Expedited Passport Services in Racine County, WI",
  heroSubtitle: "Racine County stretches along the Lake Michigan shoreline in southeastern Wisconsin, nestled between Milwaukee to the north and Chicago to the south. Fast Passport Center's registered couriers deliver your documents to the Chicago Passport Agency at 101 Ida B Wells Drive so you get a new or renewed passport as fast as 24 hours — whether you're a Mount Pleasant professional heading to an international conference, a Sturtevant family planning a European vacation, or a Burlington resident needing urgent travel documents for an overseas emergency.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Racine%20County%20Wisconsin%20landscape%20with%20Lake%20Michigan%20shoreline%20views%20and%20historic%20Wind%20Point%20Lighthouse%20during%20golden%20afternoon%20light%2C%20Midwestern%20county%20scenery%20with%20blend%20of%20lakeshore%20communities%20and%20inland%20farmland%2C%20wide%20angle%20professional%20photography%20with%20clean%20natural%20colors&width=1800&height=700&seq=racine-county-wi-hero-001&orientation=landscape",
  heroImageAlt: "Racine County Wisconsin with Lake Michigan shoreline and Wind Point Lighthouse",
  geoPosition: "42.7261;-87.8139",
  geoPlacename: "Racine County, Wisconsin",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "101 Ida B Wells Drive, Chicago, IL 60605",
    distance: "Approximately 65 miles from Racine County — about 1 hour 15 minutes via I-94 South",
  },
  nearbyCitiesZips: [
    { name: "Racine", zips: "53401, 53402, 53403, 53404, 53405, 53406" },
    { name: "Mount Pleasant", zips: "53406, 53177" },
    { name: "Caledonia", zips: "53108, 53402" },
    { name: "Sturtevant", zips: "53177" },
    { name: "Burlington", zips: "53105" },
    { name: "Union Grove", zips: "53182" },
    { name: "Waterford", zips: "53185" },
    { name: "Wind Point", zips: "53402" },
    { name: "Yorkville", zips: "53182" },
    { name: "Rochester", zips: "53167" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Chicago Passport Agency — about 65 miles from Racine County via I-94 South" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Racine-Kenosha Metro", desc: "Serving Racine, Mount Pleasant, Caledonia, Sturtevant, Burlington, and all Racine County" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Racine County case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Chicago IL", route: "/cities/chicago" },
    { name: "Expedited passport in Milwaukee WI", route: "/cities/milwaukee" },
    { name: "Urgent passport in Racine County WI", route: "/cities/racine-county" },
    { name: "Wisconsin passport hub", route: "/states/wisconsin" },
    { name: "Fast passport in Racine WI", route: "/cities/racine" },
    { name: "Expedited passport in Kenosha WI", route: "/cities/kenosha-wi" },
    { name: "Emergency passport in Madison WI", route: "/cities/madison-wi" },
  ],
  fpcOfficeAddress: "101 Ida B Wells Drive, Chicago, IL 60605",
  fpcOfficeNote: "Fast Passport Center serves Racine County remotely via secure courier. Our registered couriers hand-deliver your documents to the Chicago Passport Agency at 101 Ida B Wells Drive — approximately 65 miles south. Racine County sits directly on the I-94 corridor between Milwaukee and Chicago, making overnight courier routing to the Chicago Loop exceptionally efficient. Our dedicated agents ensure Racine County travelers never need to navigate Chicago traffic for passport processing.",
  fpcOfficeDistance: "Serving all of Racine County and the Racine-Kenosha corridor",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Racine County including Racine, Mount Pleasant, Caledonia, Sturtevant, Burlington, Union Grove, Waterford, and the entire Racine County corridor. From SC Johnson professionals to Racine County families, we help southeastern Wisconsin travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Racine County communities including Racine, Mount Pleasant, Caledonia, Sturtevant, Burlington, Union Grove, Waterford, Wind Point, and Yorkville.",
  howItWorksIntro: "Most Racine County travelers complete everything online and ship documents overnight. At approximately 65 miles from Chicago, Fast Passport Center provides fast courier turnaround for Racine County residents — eliminating the I-94 drive through Illinois construction and Chicago Loop traffic.",
  mistakesIntro: "Racine County travelers, including SC Johnson international business professionals and southeastern Wisconsin families heading abroad, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Racine County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Racine County? Here are the most common ones we hear from southeastern Wisconsin travelers.",
  statePageRoute: "/states/wisconsin",
  statePageName: "Wisconsin",
  dmvWebsite: "https://wisconsindot.gov/Pages/online-srvcs/external/bds.aspx",
  dmvLabel: "Wisconsin DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins at Ida B Wells Drive",
        "Life-or-death emergencies only",
        "65 miles to Chicago (1 hour 15 minutes via I-94 South)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for SC Johnson international travel",
        "Perfect for Racine County family vacations",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from ORD or MKE",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Racine County is just 65 miles from the Chicago Passport Agency — our courier saves Mount Pleasant and Caledonia travelers the I-94 drive.",
      bullets: ["Racine County coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in Racine County",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Racine County adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Racine County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support throughout Racine County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Racine County residents with DS-64 form guidance and courier routing to Chicago.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Chicago agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Racine County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Racine County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Racine County WI", description: "Urgent passport processing for Racine County, WI travelers via Chicago Passport Agency courier — 65 miles from Racine County." },
    { name: "Passport Renewal Racine County WI", description: "Fast passport renewal for Racine County, Wisconsin residents." },
    { name: "New Passport Racine County WI", description: "First-time passport application assistance for Racine County area residents." },
    { name: "Lost Passport Racine County WI", description: "Passport replacement for lost or stolen passports for Racine County residents." },
    { name: "Child Passport Racine County WI", description: "Minor passport services for Racine County families." },
    { name: "Name Change Passport Racine County WI", description: "Passport name change service for Racine County, WI residents." },
  ],
  testimonials: [
    {
      name: "Mark S.",
      location: "Mount Pleasant, WI",
      rating: 5,
      text: "SC Johnson international marketing manager who got pulled into a last-minute product launch in Bangkok. My passport was expired and I had 6 days before the flight. Fast Passport Center had me a new one in 3 business days. The courier to Chicago was seamless.",
      service: "Emergency Renewal",
    },
    {
      name: "The J Family",
      location: "Caledonia, WI",
      rating: 5,
      text: "Racine County family of four planning a summer trip to Germany to visit relatives. We needed two renewals and two child passports with only 5 weeks before departure. Fast Passport Center handled everything — all four passports arrived in 8 business days.",
      service: "Family Applications",
    },
    {
      name: "Patricia L.",
      location: "Burlington, WI",
      rating: 5,
      text: "Retired teacher living in Burlington who realized my passport was missing 14 days before a long-planned tour of the Scottish Highlands. Fast Passport Center helped with the DS-64 and couriered my replacement through Chicago. Passport in hand in 5 business days.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Kevin R.",
      location: "Sturtevant, WI",
      rating: 5,
      text: "Manufacturing engineer heading to a supplier audit in Stuttgart on one week's notice. My passport had expired. Fast Passport Center processed a renewal in 4 business days. The dedicated agent kept me updated daily — zero stress despite the tight timeline.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "rc-1",
      name: "Racine Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "603 Main St",
      city: "Racine",
      state: "WI",
      zip: "53403",
      phone: "(262) 632-2717",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Racine — Downtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Racine County residents. Appointment required. Located on Main Street in downtown Racine.",
      icon: "ri-mail-line",
    },
    {
      id: "rc-2",
      name: "Racine County Clerk Office",
      type: "County Clerk Passport Acceptance",
      address: "730 Wisconsin Ave",
      city: "Racine",
      state: "WI",
      zip: "53403",
      phone: "(262) 636-3121",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Racine — Downtown",
      website: "https://www.racinecounty.com",
      websiteLabel: "Racine County Government",
      notes: "County clerk passport acceptance services in the Racine County Courthouse. Convenient downtown location.",
      icon: "ri-government-line",
    },
    {
      id: "rc-3",
      name: "Mount Pleasant Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6100 Washington Ave",
      city: "Mount Pleasant",
      state: "WI",
      zip: "53406",
      phone: "(262) 886-3111",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Mount Pleasant — 4 miles west of Racine",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Mount Pleasant and western Racine County residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "rc-4",
      name: "Burlington Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "148 E State St",
      city: "Burlington",
      state: "WI",
      zip: "53105",
      phone: "(262) 763-2411",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Burlington — 20 miles west of Racine",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Burlington and western Racine County residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "rc-5",
      name: "Kenosha Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "5605 Sheridan Rd",
      city: "Kenosha",
      state: "WI",
      zip: "53140",
      phone: "(262) 656-1111",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "Kenosha — 10 miles south of Racine",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Large facility serving Kenosha County and southern Racine County. More appointment availability than Racine locations.",
      icon: "ri-mail-line",
    },
    {
      id: "rc-6",
      name: "Union Grove Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1100 Main St",
      city: "Union Grove",
      state: "WI",
      zip: "53182",
      phone: "(262) 878-2300",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Union Grove — 15 miles west of Racine",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Small-town option for western Racine County residents. Call ahead — limited hours may vary.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Chicago Agency", agency: "65 miles — 1 hour 15 minute drive via I-94 South, plus Chicago Loop traffic", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Assuming Milwaukee Has a Passport Agency",
      description: "Racine County residents are just 25 miles from Milwaukee and often assume a passport agency exists there. The closest passport agency is in Chicago — 65 miles south. No passport agency exists in Milwaukee or anywhere in Wisconsin, leaving Racine County travelers with no in-state emergency processing option except registered courier services.",
    },
    {
      icon: "ri-calendar-line",
      title: "SC Johnson Professionals Not Factoring in International Assignment Timelines",
      description: "Racine is home to SC Johnson's global headquarters. Employees offered international assignments often have tight timelines. Start passport processing immediately upon accepting an international role — do not wait until other relocation logistics are handled. Passport and visa processing can take weeks beyond the passport itself.",
    },
    {
      icon: "ri-global-line",
      title: "Confusing County Clerk Services With Expediting",
      description: "Racine County residents sometimes assume the Racine County Clerk's office can expedite passport applications. County clerks are passport acceptance facilities only — they accept DS-11 applications and verify documents but cannot speed up processing. Expediting requires registered courier access to a passport agency.",
    },
    {
      icon: "ri-restart-line",
      title: "Not Checking Passport Validity Before Booking From ORD or MKE",
      description: "Racine County travelers typically fly from Chicago O'Hare (ORD) or Milwaukee Mitchell (MKE). Many book international flights months ahead without checking passport expiration dates. Most countries require 6 months passport validity beyond your return date. If your passport expires within 6 months of travel, airlines may deny boarding.",
    },
    {
      icon: "ri-image-line",
      title: "Using I-94 Rest Stop Photos as Passport Photos",
      description: "Racine County residents sometimes try to use drugstore photos taken hastily before driving to Chicago. Passport photos must be exactly 2x2 inches with a pure white background, neutral expression, and no glasses — only verified passport photo services meet U.S. Department of State standards.",
    },
    {
      icon: "ri-file-list-line",
      title: "Renewing by Mail When In-Person Is Required",
      description: "Racine County residents with passports that expired more than 5 years ago, were issued under age 16, or have a name change without documentation must use Form DS-11 (in-person application) — not DS-82 (mail-in). Filing the wrong form means your application gets rejected and you start over.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Racine, Mount Pleasant, or Kenosha." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Chicago Passport Agency — 65 miles south via I-94." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Chicago Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Racine County address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Racine County from the Chicago passport agency?",
      a: "Racine County is approximately 65 miles from the Chicago Passport Agency at 101 Ida B Wells Drive — about a 1 hour 15 minute drive south via I-94, depending on Chicago traffic. However, the Chicago agency requires pre-scheduled NPIC appointments and does not accept walk-ins. Appointments often fill 2–3 weeks in advance.",
    },
    {
      q: "Does Racine County, WI have its own passport agency?",
      a: "No. Racine County does not have a passport agency, and neither does the entire state of Wisconsin. The closest passport agency is the Chicago Passport Agency at 101 Ida B Wells Drive, about 65 miles south. Racine County only has passport acceptance facilities — the Racine Main Post Office, Racine County Clerk's office, and Mount Pleasant Post Office — which can accept DS-11 applications but cannot process or expedite them.",
    },
    {
      q: "I work at SC Johnson — how fast can I get a passport for international business travel?",
      a: "SC Johnson professionals heading to international meetings, product launches, or supplier audits are frequent clients. Emergency processing delivers passports in 24–48 hours. Standard expedited processing runs 2–9 business days. We recommend having a valid passport at all times if your role involves potential international travel.",
    },
    {
      q: "Where do Racine County travelers typically fly internationally?",
      a: "Racine County travelers typically fly from Chicago O'Hare International Airport (ORD, about 55 miles south) or Milwaukee Mitchell International Airport (MKE, about 20 miles north). ORD offers extensive international routes as a major global hub. MKE offers domestic connections with some international service.",
    },
    {
      q: "How fast can I get an emergency passport in Racine County?",
      a: "Emergency processing is typically 24–48 hours for Racine County residents. Racine County's position directly on the I-94 corridor between Milwaukee and Chicago makes overnight courier routing to the Chicago Passport Agency extremely efficient. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Where can I get passport photos in Racine County?",
      a: "Walgreens at 3825 Durand Ave and 4810 Washington Ave in Racine, CVS Pharmacy at 1122 West Blvd, and the UPS Store at 5801 Washington Ave all offer passport photo services. In Mount Pleasant, Walgreens at 6125 Durand Ave and in Burlington, Walgreens at 184 E Chestnut St also provide photos. Always verify 2x2 inch specifications with white background.",
    },
    {
      q: "Can the Racine County Clerk's office process my passport?",
      a: "Yes. The Racine County Clerk's office at 730 Wisconsin Ave in downtown Racine accepts passport applications as a passport acceptance facility. They can accept DS-11 applications and verify your documents. Call (262) 636-3121 before visiting to confirm current passport service hours.",
    },
    {
      q: "How do I renew my passport in Racine County?",
      a: "Adult renewals meeting all DS-82 eligibility criteria (16 or older, issued within 15 years, same name) can be done entirely by mail. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than self-service USPS (8–11 weeks). If you're not eligible for mail-in renewal, visit the Racine Main Post Office or Racine County Clerk for an in-person DS-11 application.",
    },
    {
      q: "Does the Racine Main Post Office take walk-in passport appointments?",
      a: "The Racine Main Post Office at 603 Main St accepts passport applications by appointment Monday through Friday from 9:00 AM to 4:00 PM. Walk-ins are generally not accepted — call (262) 632-2717 to schedule. The post office is often busiest during lunch hours.",
    },
    {
      q: "Can Fast Passport Center help Caledonia and Sturtevant residents too?",
      a: "Absolutely. We serve all of Racine County including Racine, Mount Pleasant, Caledonia, Sturtevant, Burlington, Union Grove, Waterford, Wind Point, Yorkville, and Rochester. Residents of every Racine County community never need to drive to Chicago for service — our courier handles everything.",
    },
    {
      q: "Is it faster to go through Milwaukee or Chicago for passport processing?",
      a: "Neither Milwaukee nor any city in Wisconsin has a passport agency. The closest passport agency to Racine County is in Chicago at 101 Ida B Wells Drive — about 65 miles south. Milwaukee has acceptance facilities only, same as Racine. Fast Passport Center's courier service routes to the Chicago agency for the fastest possible processing.",
    },
    {
      q: "Is the Racine County Clerk faster than the Racine Post Office for passport applications?",
      a: "Both the Racine Main Post Office and the Racine County Clerk's office serve as passport acceptance facilities with similar processing capabilities. The County Clerk's office at 730 Wisconsin Ave often has more flexible hours (8:00 AM–4:30 PM vs. the post office's 9:00 AM–4:00 PM). However, neither facility can expedite processing — that's where Fast Passport Center's courier service comes in.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Racine (Durand)", address: "3825 Durand Ave, Racine, WI 53405", hours: "Mon–Sun: 7AM–10PM" },
    { name: "Walgreens — Racine (Washington)", address: "4810 Washington Ave, Racine, WI 53406", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Racine", address: "1122 West Blvd, Racine, WI 53405", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Mount Pleasant", address: "5801 Washington Ave, Mount Pleasant, WI 53406", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–2PM" },
    { name: "Walgreens — Mount Pleasant", address: "6125 Durand Ave, Mount Pleasant, WI 53406", hours: "Mon–Sun: 7AM–10PM" },
    { name: "Walgreens — Burlington", address: "184 E Chestnut St, Burlington, WI 53105", hours: "Mon–Sun: 7AM–10PM" },
  ],
  dmvLocations: [
    { name: "WI DMV — Racine", address: "9531 Rayne Rd, Sturtevant, WI 53177", hours: "Mon–Fri: 8:00AM–4:30PM", note: "Primary DMV service center for Racine County. REAL ID, driver's license, and state ID services. Located in Sturtevant near I-94.", featured: true },
    { name: "WI DMV — Kenosha", address: "4910 88th Ave, Kenosha, WI 53144", hours: "Mon–Fri: 8:00AM–4:30PM", note: "Kenosha County DMV. About 15 miles south of Racine. Convenient for southern Racine County residents.", featured: false },
    { name: "WI DMV — Milwaukee (South)", address: "2738 W Forest Home Ave, Milwaukee, WI 53215", hours: "Mon–Fri: 8:00AM–4:30PM", note: "Larger DMV in Milwaukee. About 25 miles north of Racine.", featured: false },
  ],
  airports: [
    {
      code: "MKE",
      name: "Milwaukee Mitchell International Airport",
      distance: "20 miles north of Racine County",
      address: "5300 S Howell Ave, Milwaukee, WI 53207",
      note: "Wisconsin's largest airport with domestic routes via major carriers. About 25 minutes via I-94 North. The most convenient departure point for Racine County travelers heading to domestic connecting hubs.",
      flightUrl: "https://www.mitchellairport.com/",
      flightLabel: "MKE Airport",
      featured: true,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "55 miles south of Racine County",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "One of the world's busiest airports with direct international flights worldwide. About 1 hour via I-94 South. Major international gateway for Racine County and southeastern Wisconsin travelers.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "ORD Airport",
      featured: false,
    },
    {
      code: "MDW",
      name: "Chicago Midway International Airport",
      distance: "65 miles south of Racine County",
      address: "5700 S Cicero Ave, Chicago, IL 60638",
      note: "Chicago's secondary airport with extensive domestic routes. About 1 hour 15 minutes via I-94. Good alternative to O'Hare for domestic connections.",
      flightUrl: "https://www.flychicago.com/midway/",
      flightLabel: "MDW Airport",
      featured: false,
    },
    {
      code: "MKE",
      name: "Kenosha Regional Airport",
      distance: "15 miles south of Racine County",
      address: "9900 52nd St, Kenosha, WI 53144",
      note: "General aviation airport serving Kenosha County. No commercial passenger service but convenient for private and charter flights.",
      flightUrl: "https://www.kenosha.org/departments/airport",
      flightLabel: "ENW Airport",
      featured: false,
    },
  ],
};